import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { WorldMap } from '../components/WorldMap';
import {
  ArrowRight,
  Phone,
  Upload,
  Globe,
  FileText,
  Languages,
  TrendingUp,
  CheckCircle2
} from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Card } from '../components/ui/card';

export const CountriesPage = () => {
  const regions = [
    {
      id: 'africa',
      name: 'Africa',
      subtitle: 'East & West African Markets',
      color: '#606090',
      gradient: 'linear-gradient(135deg, #606090 0%, #7080A0 100%)',
      countries: [
        { name: 'Kenya', slug: 'kenya' },
        { name: 'Nigeria', slug: 'nigeria-pharma-nutra-registration' },
        { name: 'Tanzania', slug: 'tanzania' },
        { name: 'Uganda', slug: 'uganda-pharma-nutra-registration' },
        { name: 'Benin', slug: 'benin-pharma-nutra-registration' },
        { name: 'Senegal', slug: 'senegal-pharma-nutra-registration' },
        { name: 'Togo', slug: 'togo-pharma-nutra-registration' },
        { name: 'Mozambique', slug: 'mozambique-pharma-nutra-registration' },
        { name: 'Ghana', slug: 'ghana-pharma-nutra-registration' }
      ]
    },
    {
      id: 'asia',
      name: 'Asia',
      subtitle: 'South & Southeast Asia',
      color: '#7090A0',
      gradient: 'linear-gradient(135deg, #7090A0 0%, #80A0B0 100%)',
      countries: [
        { name: 'Cambodia', slug: 'cambodia' },
        { name: 'Myanmar', slug: 'myanmar-pharma-nutra-registration' },
        { name: 'Sri Lanka', slug: 'sri-lanka-pharma-nutra-registration' },
        { name: 'Nepal', slug: 'nepal-pharma-nutra-registration' },
        { name: 'Mauritius', slug: 'mauritius-pharma-nutra-registration' },
        { name: 'Malaysia', slug: 'malaysia-pharma-nutra-registration' },
        { name: 'Bangladesh', slug: 'bangladesh-pharma-nutra-registration' },
        { name: 'Bhutan', slug: 'bhutan-pharma-nutra-registration' },
        { name: 'Maldives', slug: 'maldives-pharma-nutra-registration' }
      ]
    },
    {
      id: 'gcc',
      name: 'GCC',
      subtitle: 'Gulf Cooperation Council',
      color: '#A060B0',
      gradient: 'linear-gradient(135deg, #A060B0 0%, #B070C0 100%)',
      countries: [
        { name: 'Bahrain', slug: 'bahrain-pharma-nutra-registration' },
        { name: 'Kuwait', slug: 'kuwait' },
        { name: 'Oman', slug: 'oman-pharma-nutra-registration' },
        { name: 'Qatar', slug: 'qatar-pharma-nutra-registration' },
        { name: 'Saudi Arabia', slug: 'saudi-arabia-pharma-nutra-registration' },
        { name: 'UAE', slug: 'united-arab-emirates-pharma-nutra-registration' }
      ]
    },
    {
      id: 'latam',
      name: 'Latin America',
      subtitle: 'Central & South America',
      color: '#708080',
      gradient: 'linear-gradient(135deg, #708080 0%, #809090 100%)',
      countries: [
        { name: 'Mexico', slug: 'mexico-pharma-nutra-registration' },
        { name: 'Colombia', slug: 'colombia-pharma-nutra-registration' },
        { name: 'Peru', slug: 'peru-pharma-nutra-registration' },
        { name: 'Chile', slug: 'chile-pharma-nutra-registration' },
        { name: 'Dominican Republic', slug: 'dominican-republic-pharma-nutra-registration' },
        { name: 'Panama', slug: 'panama-pharma-nutra-registration' },
        { name: 'Paraguay', slug: 'paraguay-pharma-nutra-registration' },
        { name: 'Costa Rica', slug: 'costa-rica-pharma-nutra-registration' },
        { name: 'Uruguay', slug: 'uruguay-pharma-nutra-registration' }
      ]
    }
  ];

  const benefits = [
    {
      icon: FileText,
      title: 'Different Document Formats',
      description: 'LATAM, GCC, Africa & Asia follow different dossier styles — we adapt documentation accordingly.',
      color: '#606090'
    },
    {
      icon: Languages,
      title: 'Language & Artwork Requirements',
      description: 'Bilingual labels, Arabic/French/Spanish requirements vary widely.',
      color: '#7090A0'
    },
    {
      icon: Globe,
      title: 'Category Classification',
      description: 'Drug vs supplement vs food classification is region-dependent — handled correctly from day one.',
      color: '#A060B0'
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Countries: Kenya Tanzania Uganda Cambodia Nepal | Pharma Registration Africa Asia</title>
        <meta
          name="description"
          content="Pharma product registration in 30+ countries: Kenya PPB, Tanzania TMDA, Cambodia, Nepal DDA, Uganda NDA, Sri Lanka NMRA. Country-specific regulatory guidance for exports."
        />
        <meta name="keywords" content="Kenya pharmacy board registration, Tanzania TMDA registration, Uganda NDA registration, Cambodia ACTD dossier, Nepal DDA registration, pharma registration Africa, ASEAN pharma market entry, country specific regulatory guidance, export market selection" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://www.theapostrophe.com/countries" />
        
        <meta property="og:title" content="30+ Countries | Pharma Registration Africa Asia GCC LATAM" />
        <meta property="og:description" content="Country-specific regulatory guidance for pharma exports to Africa, Asia, GCC, and Latin America." />
        <meta property="og:url" content="https://www.theapostrophe.com/countries" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.theapostrophe.com/og-image.jpg" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="30+ Countries | Pharma Registration Guide" />
        <meta name="twitter:description" content="Country-specific regulatory guidance for pharmaceutical exports." />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Which countries offer the fastest pharmaceutical registration timelines?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Fast-track markets include Cambodia (3-6 months), Kenya (6-9 months with complete documentation), and some GCC countries like Bahrain and Oman (6-12 months). However, timeline heavily depends on dossier completeness and whether you have a reliable local importer. Countries like Tanzania and Uganda may take 12-18 months due to rigorous review processes."
                }
              },
              {
                "@type": "Question",
                "name": "Do I need a local distributor or importer for pharmaceutical registration?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, in most countries. GCC, LATAM, Africa, and ASEAN markets require a local importer or distributor to hold the registration license and file submissions with the Ministry of Health. The importer acts as the legal representative and handles post-approval compliance."
                }
              },
              {
                "@type": "Question",
                "name": "How do I choose which countries to enter first?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Market selection depends on: disease burden alignment, regulatory complexity, timeline requirements, and commercial potential. Our Groundwork Pack provides a structured feasibility assessment with country scoring, SKU prioritization, and recommended launch sequence based on your product portfolio and business objectives."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6 overflow-hidden">
        <div className="max-w-6xl mx-auto text-center">
          <Badge className="mb-6 px-6 py-2 text-sm" style={{ backgroundColor: '#A060B0', color: 'white' }}>
            Global Presence
          </Badge>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight" style={{ color: '#303040' }}>
            Countries We Serve
          </h1>
          <p className="text-2xl mb-12 leading-relaxed" style={{ color: '#708080' }}>
            Pharma & nutraceutical regulatory expertise across high-growth regions — LATAM, GCC, Africa & Asia.
          </p>
          <a 
            href="#explore-regions"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('explore-regions')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            <Button
              size="lg"
              className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105 cursor-pointer"
              style={{ backgroundColor: '#606090', color: 'white' }}
            >
              Explore Country Pages
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>

      {/* Intro Block */}
      <section className="py-24 px-6 overflow-hidden" style={{ backgroundColor: 'rgba(96, 96, 144, 0.08)' }}>
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-8" style={{ color: '#303040' }}>
            Global Reach. Region-Specific Expertise.
          </h2>
          <p className="text-xl leading-relaxed mb-6" style={{ color: '#708080' }}>
            Each region follows its own regulatory systems, documentation styles, and submission expectations.
            Our country-specific processes ensure your CTD/ACTD dossiers, artworks, translations, and query responses match exactly what local authorities require.
          </p>
          <p className="text-lg font-medium" style={{ color: '#606090' }}>
            Navigate below to explore regulatory details for each country.
          </p>
        </div>
      </section>

      {/* Region Panels */}
      <section id="explore-regions" className="py-24 px-6 overflow-hidden" style={{ backgroundColor: 'rgba(112, 128, 128, 0.05)' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-16 text-center" style={{ color: '#303040' }}>
            Explore by Region
          </h2>
          
          <div className="space-y-8">
            {regions.map((region, index) => (
              <div
                key={region.name}
                className={`bg-white rounded-3xl shadow-xl overflow-hidden transition-all duration-300 hover:shadow-2xl ${
                  index % 2 === 0 ? 'hover:translate-x-2' : 'hover:-translate-x-2'
                }`}
              >
                {/* Region Header */}
                <Link
                  to={`/regions/${region.id}`}
                  className="block p-8 lg:p-12 cursor-pointer group"
                  style={{ background: `linear-gradient(135deg, ${region.color} 0%, ${region.color}dd 100%)` }}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex-1">
                      <h3 className="text-4xl font-bold text-white mb-2 group-hover:underline underline-offset-4 transition-all">
                        {region.name}
                        <ArrowRight className="inline-block ml-3 h-8 w-8 opacity-0 group-hover:opacity-100 group-hover:translate-x-2 transition-all duration-300" />
                      </h3>
                      <p className="text-xl text-white/90 mb-4">{region.description}</p>
                    </div>
                    <div className="mt-4 lg:mt-0 flex flex-col lg:flex-row items-start lg:items-center gap-4">
                      <div className="text-center">
                        <div className="text-5xl font-bold text-white/80">{region.countries.length}</div>
                        <p className="text-lg text-white/70">Countries</p>
                      </div>
                      <div
                        className="px-6 py-3 rounded-full transition-all duration-300 group-hover:scale-105 group-hover:shadow-xl whitespace-nowrap font-medium flex items-center"
                        style={{ backgroundColor: 'white', color: region.color }}
                      >
                        View Region Details
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </div>
                    </div>
                  </div>
                </Link>

                {/* Country Grid */}
                <div className="p-8 lg:p-12">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {region.countries.map((country) => (
                      <Link
                        key={country.slug}
                        to={`/country/${country.slug}`}
                        className="group block p-4 rounded-xl bg-gray-50 transition-all duration-300 hover:bg-gray-100 hover:shadow-md hover:-translate-y-1"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-semibold" style={{ color: '#303040' }}>
                            {country.name}
                          </span>
                          <ArrowRight
                            className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                            style={{ color: region.color }}
                          />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Strip */}
      <section className="py-24 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-16 text-center" style={{ color: '#303040' }}>
            Why Region-Specific Regulatory Expertise Matters
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="text-center"
              >
                <div
                  className="w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center transition-transform duration-300 hover:scale-110"
                  style={{ backgroundColor: benefit.color + '20' }}
                >
                  <benefit.icon className="h-12 w-12" style={{ color: benefit.color }} />
                </div>
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#303040' }}>
                  {benefit.title}
                </h3>
                <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-16 px-6 overflow-hidden" style={{ backgroundColor: '#303040' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold mb-2" style={{ color: '#A060B0' }}>
                30+
              </div>
              <div className="text-lg text-white/80">Countries Served</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2" style={{ color: '#7090A0' }}>
                4
              </div>
              <div className="text-lg text-white/80">Major Regions</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2" style={{ color: '#606090' }}>
                200+
              </div>
              <div className="text-lg text-white/80">Products Launched</div>
            </div>
            <div className="group relative">
              <div className="text-5xl font-bold mb-2" style={{ color: '#708080' }}>
                15+
              </div>
              <div className="text-lg text-white/80 flex items-center justify-center gap-1">
                Languages Supported
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full ml-1 cursor-help" title="Artwork, labels & regulatory translations">ℹ</span>
              </div>
              {/* Tooltip on hover */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 bg-white text-gray-800 text-sm p-3 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10">
                <strong className="block mb-1">Translation & Localization Support:</strong>
                Arabic, French, Spanish, Portuguese, Khmer, Burmese, Nepali, Sinhala, Bahasa, Thai, Vietnamese, Amharic, Swahili, and more for regulatory documents, labels, and artwork.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Market Selection Resources */}
      <section className="py-24 px-6 overflow-hidden" style={{ backgroundColor: 'rgba(96, 96, 144, 0.05)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#303040' }}>
              Strategic Market Entry Support
            </h2>
            <p className="text-xl" style={{ color: '#708080' }}>
              From market selection to submission-ready dossiers
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <Link to="/success-packs/groundwork-pack">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl p-8 h-full cursor-pointer group">
                <div className="flex flex-col h-full">
                  <div className="w-12 h-12 rounded-xl mb-6 flex items-center justify-center" style={{ backgroundColor: '#A060B020' }}>
                    <TrendingUp className="h-6 w-6" style={{ color: '#A060B0' }} />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 group-hover:text-opacity-80 transition-colors" style={{ color: '#A060B0' }}>
                    Groundwork Pack
                  </h3>
                  <p className="mb-6 flex-grow" style={{ color: '#708080' }}>
                    <strong>Market selection + compliance roadmap</strong> for your SKUs. Country-specific regulatory guidance, SKU screening, documentation checklist. Perfect for <strong>first-time exporters</strong>.
                  </p>
                  <div className="flex items-center gap-2" style={{ color: '#A060B0' }}>
                    <span className="font-semibold">Learn More</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </Card>
            </Link>
            <Link to="/services">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl p-8 h-full cursor-pointer group">
                <div className="flex flex-col h-full">
                  <div className="w-12 h-12 rounded-xl mb-6 flex items-center justify-center" style={{ backgroundColor: '#60609020' }}>
                    <CheckCircle2 className="h-6 w-6" style={{ color: '#606090' }} />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 group-hover:text-opacity-80 transition-colors" style={{ color: '#606090' }}>
                    Regulatory Services
                  </h3>
                  <p className="mb-6 flex-grow" style={{ color: '#708080' }}>
                    <strong>CTD/ACTD dossier preparation</strong>, artwork compliance, API/DMF documentation, translations, and deficiency handling for all regions.
                  </p>
                  <div className="flex items-center gap-2" style={{ color: '#606090' }}>
                    <span className="font-semibold">View Services</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-6 overflow-hidden">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#303040' }}>
              Country-Specific Registration FAQs
            </h2>
            <p className="text-xl" style={{ color: '#708080' }}>
              Common questions about pharmaceutical registration across different markets
            </p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="faq-1" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-8 py-6 hover:no-underline text-left">
                <span className="text-lg font-bold pr-8" style={{ color: '#303040' }}>
                  Which countries offer the fastest pharmaceutical registration timelines?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                  <strong>Fast-track markets</strong> include Cambodia (3-6 months), Kenya (6-9 months with complete documentation), and some GCC countries like Bahrain and Oman (6-12 months). However, timeline heavily depends on <strong>dossier completeness</strong> and whether you have a reliable local importer. Countries like Tanzania and Uganda may take 12-18 months due to rigorous review processes.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-2" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-8 py-6 hover:no-underline text-left">
                <span className="text-lg font-bold pr-8" style={{ color: '#303040' }}>
                  Do I need a local distributor or importer for pharmaceutical registration?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                  Yes, in most countries. <strong>GCC, LATAM, Africa, and ASEAN</strong> markets require a local importer or distributor to hold the registration license and file submissions with the Ministry of Health. The importer acts as the legal representative and handles post-approval compliance. We coordinate with your importer to ensure submission readiness and documentation alignment.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-3" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-8 py-6 hover:no-underline text-left">
                <span className="text-lg font-bold pr-8" style={{ color: '#303040' }}>
                  What is the difference between East African Community (EAC) and individual country registrations?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                  The <strong>East African Community (EAC)</strong> includes Kenya, Tanzania, Uganda, Rwanda, Burundi, and South Sudan. While EAC aims for <strong>harmonized registration</strong>, each country still maintains its own regulatory authority (PPB Kenya, TMDA Tanzania, NDA Uganda). Currently, a product approved in one EAC country still requires separate registration in others, though <strong>dossier sharing and reliance pathways</strong> are emerging. We provide country-specific CTD/ACTD dossiers aligned with each authority's requirements.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-4" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-8 py-6 hover:no-underline text-left">
                <span className="text-lg font-bold pr-8" style={{ color: '#303040' }}>
                  Which countries require Arabic or French translations for pharmaceutical labeling?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                  <strong>Arabic translations</strong> are mandatory in all GCC countries (Saudi Arabia, UAE, Qatar, Kuwait, Bahrain, Oman). <strong>French translations</strong> are required in West African markets like Benin, Senegal, Togo, and some Central African countries. <strong>Spanish</strong> is required across LATAM (Mexico, Colombia, Peru, Chile). Some countries accept English as primary with local language as secondary. We provide regulatory-grade translations with proper terminology verification.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-5" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-8 py-6 hover:no-underline text-left">
                <span className="text-lg font-bold pr-8" style={{ color: '#303040' }}>
                  Can a nutraceutical product be registered as a pharmaceutical in some countries?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                  Yes. <strong>Product classification varies by country</strong>. A multivitamin may be registered as a <strong>pharmaceutical product</strong> in Kenya or Tanzania but as a <strong>food supplement</strong> in Cambodia or Sri Lanka. Some products with therapeutic claims must follow pharma pathways even if categorized as nutraceuticals in the origin country. We perform <strong>category classification analysis</strong> during the Groundwork Pack to determine the correct pathway for each target market.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-6" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-8 py-6 hover:no-underline text-left">
                <span className="text-lg font-bold pr-8" style={{ color: '#303040' }}>
                  How do I choose which countries to enter first?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                  <strong>Market selection</strong> depends on: disease burden alignment, regulatory complexity, timeline requirements, and commercial potential. Our <strong>Groundwork Pack</strong> provides a structured <strong>feasibility assessment</strong> with country scoring, SKU prioritization, and recommended launch sequence based on your product portfolio and business objectives.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6 overflow-hidden">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#303040' }}>
            Not Sure Which Countries Fit Your Products?
          </h2>
          <p className="text-xl mb-12" style={{ color: '#708080' }}>
            Send your product list and receive a region-wise regulatory feasibility assessment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true" 
              target="_blank" 
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open('https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true', '_blank', 'noopener,noreferrer');
              }}
            >
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#A060B0', color: 'white' }}
              >
                Book a Call
                <Phone className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <Link to="/contact#send-us-your-details">
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ borderColor: '#606090', color: '#606090' }}
              >
                Upload Product List
                <Upload className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
